import { Alert, Box, Typography } from "@mui/material";
import * as React from "react";
import { AvatarImage } from "ui/Scripts/source/components/framework/Avatar/AvatarImage";
import { Translations } from "ui/Scripts/source/models/translations";
import { Base } from "../../../../../../QTeamSystems.QTeamIntra.Ui/Scripts/source/framework/base";
import { AppUtils } from "../../../framework/appUtils";
import { SaveDataObj } from "../../../framework/saveData";
import * as companyGroupService from "../../../models/services/companyGroupService";
import { ActionContainer } from "../../framework/ActionContainer";
import { List } from "../../framework/List/List";
import { ListIconButtonsItem } from "../../framework/List/ListIconButtons/ListIconButtonsItem/ListIconButtonsItem";
import { ListSearchFilter } from "../../framework/List/ListSearchFilter";
import { CompanyListItem, ICompanyListItem } from "source/models/company/companyListItem";
import { CompanyActiveState, EnumHelper } from "ui/Scripts/source/models/enums";
import { SelectCompanyDialog } from "source/components/company/SelectCompanyDialog/SelectCompanyDialog";
import { VerticalContainer } from "ui/Scripts/source/components/framework/VerticalContainer";

export interface ICompanyGroupEditDialogCompanyListProp {
    isReadOnly: boolean;
    companyGroupId: string;
    companies: ICompanyListItem[];
    onRemoved: (ids: string[]) => void;
    onAdded: (users: ICompanyListItem[]) => void;
}

interface ICompanyGroupEditDialogCompanyListState {
    // List
    isLoading: boolean;
    companies: ICompanyListItem[];
    filter: string;
    sortColumn: string;
    sortOrderIsAsc: boolean;
    selectedId: string;
    checkedIds: string[];

    // Dialog
    showSelectCompanyDialog: boolean;
}

export class CompanyGroupEditDialogCompanyList extends React.Component<ICompanyGroupEditDialogCompanyListProp, ICompanyGroupEditDialogCompanyListState> {
    constructor(props: ICompanyGroupEditDialogCompanyListProp) {
        super(props);
        this.state = {
            isLoading: false,
            companies: props.companies ? props.companies.slice(0) : [],
            filter: "",
            sortColumn: "code",
            sortOrderIsAsc: true,
            selectedId: null,
            checkedIds: [],
            showSelectCompanyDialog: false,
        };
    }

    filteredList = (filter: string, column: string, asc: boolean) => {
        const props = this.props;
        let companies = props.companies ? props.companies.slice(0) : [];
        if (filter) {
            const lowFilter = filter.toLowerCase();
            companies = companies.filter(i => i.code.toLowerCase().indexOf(lowFilter) > -1 || i.name.toLowerCase().indexOf(lowFilter) > -1);
        }
        CompanyListItem.sortCompanyListItems(companies, column, asc);
        this.setState({
            filter,
            sortColumn: column,
            sortOrderIsAsc: asc,
            companies,
        });
    };

    componentDidUpdate(prevProps: ICompanyGroupEditDialogCompanyListProp, prevState: ICompanyGroupEditDialogCompanyListState) {
        const props = this.props;
        const state = this.state;
        if (Base.equalStrArrays(prevProps.companies ? prevProps.companies.map(i => i.id) : [], props.companies ? props.companies.map(i => i.id) : [])) return;
        this.filteredList(state.filter, state.sortColumn, state.sortOrderIsAsc);
    }

    changeFilter = (filter: string) => {
        const state = this.state;
        this.filteredList(filter, state.sortColumn, state.sortOrderIsAsc);
    };

    handleSearchLines = (sortColumn: string, sortOrderIsAsc: boolean, nextPage: boolean) => {
        const state = this.state;
        this.filteredList(state.filter, sortColumn, sortOrderIsAsc);
    };

    handleSetSelectedIds = (selectedId: string, checkedIds: string[]) => {
        this.setState({
            selectedId,
            checkedIds
        });
    };

    getRemoveSaveData = (props: ICompanyGroupEditDialogCompanyListProp, companyIds: string[]): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("id", props.companyGroupId);
        result.append("companyIds", companyIds);
        return result;
    };

    handleRemove = async () => {
        const props = this.props;
        const state = this.state;
        const selectedIds = List.getSelectedIds(state.selectedId, state.checkedIds);
        if (selectedIds.length < 1) return;
        const saveData = this.getRemoveSaveData(props, selectedIds);
        // Call server
        const result = await AppUtils.callService(() => companyGroupService.companyGroupEditRemoveCompanies(saveData.obj));
        if (!result) return;
        props.onRemoved(selectedIds);
    };
    
    handleAdd = () => {
        this.setState({
            showSelectCompanyDialog: true
        });
    };

    getAddSaveData = (props: ICompanyGroupEditDialogCompanyListProp, companyIds: string[]): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("id", props.companyGroupId);
        result.append("companyIds", companyIds);
        return result;
    };

    onSelectCompanyDialogOk = async (companies: ICompanyListItem[]) => {
        const props = this.props;
        if (!companies || companies.length < 1) return;
        this.setState({
            showSelectCompanyDialog: false
        });
        const saveData = this.getAddSaveData(this.props, companies.map(i => i.id));
        // Call server
        const result = await AppUtils.callService(() => companyGroupService.companyGroupEditAddCompanies(saveData.obj));
        if (!result) return;
        props.onAdded(companies);
    };

    onSelectCompanyDialogCancel = () => {
        this.setState({
            showSelectCompanyDialog: false
        });
    };

    render() {
        const props = this.props;
        const state = this.state;
        return (<>
            <VerticalContainer
                before={<>
                    <ActionContainer
                        leftItems={
                            <ListSearchFilter
                                searchFilter={state.filter}
                                onSearchClick={this.changeFilter} />
                        }
                        rightItems={
                            <ListIconButtonsItem
                                addTooltip={Translations.AddCompany}
                                removeTooltip={Translations.RemoveCompany}
                                addDisabled={props.isReadOnly}
                                removeDisabled={!state.selectedId && state.checkedIds.length < 1}
                                onAdd={this.handleAdd}
                                onRemove={this.handleRemove}
                            />
                        }
                    />
                    {!!props.isReadOnly &&
                        <Alert severity="info">{Translations.NewCompanyGroupMustBeSavedBeforeUsersCanBeAdded}</Alert>
                    }
                </>}
                main={<>
                    {!props.isReadOnly &&
                        <List
                            intialized={true}
                            isLoading={state.isLoading}
                            hasMore={false}
                            globalLoadingInUse={false}
                            height="100%"
                            header={{
                                columns: [
                                    { size: 5, column: "code", content: Translations.Code },
                                    { size: 5, column: "name", content: Translations.Name },
                                    { size: 2, column: "activeState", content: Translations.ActiveState }
                                ]
                            }}
                            lines={
                                state.companies.map((company) => {
                                    return {
                                        columns: [
                                            {
                                                size: 5,
                                                column: "code",
                                                content:
                                                    <Box display="flex" alignItems="center">
                                                        <AvatarImage
                                                            id={company.code}
                                                            name={company.name}
                                                            image={company.logo}
                                                            tooltip={company.name}
                                                        />
                                                        <Typography variant="body2" sx={{ marginLeft: ".5rem" }}>{company.code}</Typography>
                                                    </Box>
                                            },
                                            { size: 5, column: "name", content: company.name },
                                            { size: 2, column: "activeState", content: company.activeStateStr },
                                        ],
                                        id: company.id,
                                        classes: EnumHelper.isEqual(company.activeState, CompanyActiveState.NotInUse) ? "disabled" : ""
                                    };
                                })
                            }
                            noDataMessage={Translations.AddCompanies}
                            sortColumn={state.sortColumn}
                            sortOrderIsAsc={state.sortOrderIsAsc}
                            selectedId={state.selectedId}
                            checkedIds={state.checkedIds}
                            onSetSelectedIds={this.handleSetSelectedIds}
                            onSearchLines={this.handleSearchLines}
                        />
                    }
                </>}
            />
            {state.showSelectCompanyDialog &&
                <SelectCompanyDialog
                    multiSelect={true}
                    ignoreCompanyIds={props.companies.map(i => i.id)}
                    onOk={this.onSelectCompanyDialogOk}
                    onCancel={this.onSelectCompanyDialogCancel}
                />
            }
        </>);
    }
}